import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DataService} from '../../../Services/data.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';
import {StorageService} from '../../../Services/storage.service';

@Component({
  selector: 'app-crm-opportunity-detail',
  templateUrl: './crm-opportunity-detail.component.html',
  styleUrls: ['./crm-opportunity-detail.component.css'],
  standalone: false
})
export class CrmOpportunityDetailComponent implements OnInit {

  opportunityId = '';
  isNew = false;
  isLoading = true;
  isSaving = false;

  opportunity: any = null;
  interactions: any[] = [];
  events: any[] = [];

  // Inline edit
  editing = false;
  editModel: any = {};

  // Log Interaction modal
  addingInteraction = false;

  // New opportunity form
  newModel: any = {
    title: '',
    entityId: '',
    companyId: '',
    stageName: 'Prospect',
    stageId: null,
    status: 'open',
    opportunityType: 'new_business',
    estimatedValue: null,
    probability: 0,
    expectedCloseDate: '',
    source: '',
    description: '',
  };
  entities: any[] = [];
  companies: any[] = [];

  stages: any[] = [];
  users: any[] = [];

  readonly statuses = ['open', 'won', 'lost', 'abandoned'];
  readonly opportunityTypes = ['new_business', 'upsell', 'renewal', 'vendor_negotiation'];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataService,
    private notification: BsToastService,
    private titleService: Title,
    private localStorage: StorageService
  ) {
  }

  ngOnInit(): void {
    this.opportunityId = this.route.snapshot.paramMap.get('id') ?? '';
    this.isNew = this.opportunityId === 'new';
    if (this.isNew) {
      this.titleService.setTitle('New Opportunity');
      this.isLoading = false;
      // Pre-populate from query params (e.g. coming from customer detail)
      this.newModel.entityId = this.route.snapshot.queryParamMap.get('entity_id') ?? '';
      this.loadEntities();
      this.loadCompanies();
      this.loadStages();
      if (this.newModel.entityId) {
        this.loadEntityCompany(this.newModel.entityId);
      }
    } else {
      this.titleService.setTitle('CRM Opportunity');
      this.loadStages();
      this.loadUsers();
      this.loadOpportunity();
    }
  }

  loadCompanies(): void {
    this.dataService.getData('company?state=active&pageNumber=0&pageSize=999999').subscribe({
      next: (result: any) => {
        this.companies = result.Data ?? result.items ?? result ?? [];
        // Pre-select company from prefs only if not already resolved from entity
        if (!this.newModel.companyId) {
          const prefs = JSON.parse(this.localStorage.StorageGet('Preferences') ?? '{}');
          this.newModel.companyId = prefs?.defaults?.company_id ?? '';
        }
      },
    });
  }

  loadEntityCompany(entityId: string): void {
    this.dataService.getData('crm/entities/' + entityId).subscribe({
      next: (result: any) => {
        const companyId = result?.company_id ?? result?.companyId;
        if (companyId) {
          this.newModel.companyId = companyId;
        }
      },
    });
  }

  loadStages(): void {
    this.dataService.getDataWithParams('crm_pipeline_stage', 'pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.stages = Array.isArray(result) ? result : (result.Data ?? result.items ?? []);
        if (this.isNew && this.stages.length > 0 && !this.newModel.stageId) {
          this.newModel.stageId = this.stages[0].id;
        }
      },
    });
  }

  loadEntities(): void {
    const prefs = JSON.parse(this.localStorage.StorageGet('Preferences') ?? '{}');
    const companyId = prefs?.defaults?.company_id ?? '';
    let params = '?page=0&pageSize=999';
    if (companyId) {
      params += '&company_id=' + encodeURIComponent(companyId);
    }
    this.dataService.getData('crm/entities' + params).subscribe({
      next: (result: any) => {
        this.entities = result?.items ?? result ?? [];
      },
    });
  }

  loadOpportunity(): void {
    this.isLoading = true;
    const url = 'crm/opportunities/' + this.opportunityId;
    this.dataService.getData(url).subscribe({
      next: (result: any) => {
        this.opportunity = result?.opportunity ?? result;
        this.interactions = result?.interactions ?? [];
        this.events = result?.events ?? [];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  startEdit(): void {
    this.editModel = {...this.opportunity};
    // Normalise snake_case API fields to camelCase for edit form bindings
    if (!this.editModel.stageId) {
      this.editModel.stageId = this.editModel.stage_id ?? null;
    }
    if (!this.editModel.opportunityType) {
      this.editModel.opportunityType = this.editModel.opportunity_type ?? null;
    }
    if (!this.editModel.estimatedValue) {
      this.editModel.estimatedValue = this.editModel.estimated_value ?? null;
    }
    if (!this.editModel.expectedCloseDate) {
      this.editModel.expectedCloseDate = this.editModel.expected_close_date ?? null;
    }
    if (!this.editModel.lostReason) {
      this.editModel.lostReason = this.editModel.lost_reason ?? null;
    }
    this.editing = true;
  }

  cancelEdit(): void {
    this.editing = false;
    this.editModel = {};
  }

  saveNew(): void {
    if (!this.newModel.companyId) {
      this.notification.showErrorToast('Company is required.');
      return;
    }
    if (!this.newModel.title?.trim()) {
      this.notification.showErrorToast('Title is required.');
      return;
    }
    this.isSaving = true;
    const payload = {
      entity_id: this.newModel.entityId,
      company_id: this.newModel.companyId,
      title: this.newModel.title,
      stage_id: this.newModel.stageId ?? null,
      opportunity_type: this.newModel.opportunityType,
      status: this.newModel.status,
      salesperson_id: this.newModel.salespersonId ?? null,
      contact_id: this.newModel.contactId ?? null,
      estimated_value: this.newModel.estimatedValue,
      probability: this.newModel.probability,
      expected_close_date: this.newModel.expectedCloseDate || null,
      source: this.newModel.source,
      description: this.newModel.description,
    };
    console.log('POST crm/opportunities payload:', payload);
    this.dataService.post('crm/opportunities', payload).subscribe({
      next: (result: any) => {
        this.notification.showSuccessToast('Opportunity created.');
        this.isSaving = false;
        const newId = result?.id ?? result?.opportunity_id ?? result?.Id;
        if (newId) {
          this.router.navigate(['/crm/opportunities', newId]);
        } else {
          this.router.navigate(['/crm/opportunities']);
        }
      },
      error: () => {
        this.notification.showErrorToast('Failed to create opportunity.');
        this.isSaving = false;
      }
    });
  }

  saveEdit(): void {
    this.isSaving = true;
    const editPayload = {
      entity_id: this.editModel.entity_id ?? this.editModel.entityId ?? null,
      company_id: this.editModel.company_id ?? this.editModel.companyId ?? null,
      title: this.editModel.title,
      stage_id: this.editModel.stageId ?? this.editModel.stage_id ?? null,
      opportunity_type: this.editModel.opportunityType ?? this.editModel.opportunity_type ?? null,
      status: this.editModel.status,
      estimated_value: this.editModel.estimatedValue ?? this.editModel.estimated_value ?? null,
      probability: this.editModel.probability,
      expected_close_date: this.editModel.expectedCloseDate ?? this.editModel.expected_close_date ?? null,
      source: this.editModel.source,
      description: this.editModel.description,
      lost_reason: this.editModel.lostReason ?? this.editModel.lost_reason ?? null,
    };
    this.dataService.putDataByArray('crm/opportunities/' + this.opportunityId, editPayload).subscribe({
      next: () => {
        this.notification.showSuccessToast('Opportunity updated.');
        this.editing = false;
        this.isSaving = false;
        this.loadOpportunity();
      },
      error: () => {
        this.notification.showErrorToast('Failed to save opportunity.');
        this.isSaving = false;
      }
    });
  }

  statusClass(status: string): string {
    const map: any = {open: 'bg-primary', won: 'bg-success', lost: 'bg-danger', abandoned: 'bg-secondary'};
    return map[status] ?? 'bg-secondary';
  }

  openNewInteraction(): void {
    this.addingInteraction = true;
  }

  onInteractionSaved(): void {
    this.addingInteraction = false;
    this.loadOpportunity();
  }

  loadUsers(): void {
    this.dataService.getApiListData('sys_user', '?pageNumber=0&pageSize=999').subscribe({
      next: (result: any) => {
        this.users = result.Data ?? result.items ?? [];
      },
    });
  }

  getUserName(userId: string): string {
    if (!userId) {
      return '—';
    }
    const u = this.users.find((x: any) => x.id === userId);
    return u ? (u.name ?? u.display_name ?? userId) : userId;
  }

  interactionTypeIcon(type: string): string {
    const map: any = {
      call: 'bi-telephone', email: 'bi-envelope', meeting: 'bi-calendar-event',
      note: 'bi-sticky', demo: 'bi-display', site_visit: 'bi-geo-alt'
    };
    return map[type] ?? 'bi-chat';
  }
}
